import { useState } from 'react';

import { hueClasses } from '@/components/hues';
import { Button, Card, PageHeading } from '@/components/PageHeading';
import { can, hasFeature } from '@core/session/capability';
import type { Session } from '@core/session/types';
import { GarmentMovementReport } from './GarmentMovementReport';
import { RentalRevenueReport } from './RentalRevenueReport';
import { StockSummaryReport } from './StockSummaryReport';

type Screen = 'index' | 'garment-movement' | 'stock-summary' | 'rental-revenue';

/**
 * Reports (spec §26, `docs/Reports_Plan.md`) — the list of the shop's reports, each opened as a
 * full page beside it by internal state, as every module's sub-screens are. A1, garments in and
 * out, and B2, the stock summary.
 */
export function ReportsPage({
  session,
  onOpen,
}: {
  session: Session;
  onOpen: (key: string, view?: string, find?: string, record?: number) => void;
}) {
  const [screen, setScreen] = useState<Screen>('index');

  if (screen === 'garment-movement') {
    return (
      <GarmentMovementReport session={session} onBack={() => setScreen('index')} onOpen={onOpen} />
    );
  }

  if (screen === 'stock-summary') {
    return <StockSummaryReport session={session} onBack={() => setScreen('index')} />;
  }

  if (screen === 'rental-revenue') {
    return <RentalRevenueReport session={session} onBack={() => setScreen('index')} />;
  }

  const stock = hasFeature(session, 'UnitInventory');
  // Revenue is money: offered only to a session that may see it, so nobody opens a refusal.
  const revenue = can(session, 'Report:ViewFinancials');

  return (
    <>
      <PageHeading
        eyebrow="Reports"
        title="Reports"
        subtitle="Read a stretch of days at once, print it, or take it to Excel."
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <Card>
          <div className="flex items-start gap-3">
            <span
              className={`flex size-10 shrink-0 items-center justify-center rounded-md ${hueClasses.orange.tile}`}
              aria-hidden="true"
            >
              <i className="icon icon-arrow-left-right text-lg text-white" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                Garments in and out
              </p>
              <p className="text-default mb-3 text-[length:var(--scale-list-meta)]">
                Day by day: handed over and taken back, to and from the cleaner and the tailor, and
                new stock booked in.
              </p>
              <Button
                variant="primary"
                disabled={!stock}
                onClick={() => setScreen('garment-movement')}
              >
                Open
              </Button>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-start gap-3">
            <span
              className={`flex size-10 shrink-0 items-center justify-center rounded-md ${hueClasses.orange.tile}`}
              aria-hidden="true"
            >
              <i className="icon icon-boxes text-lg text-white" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                Stock summary
              </p>
              <p className="text-default mb-3 text-[length:var(--scale-list-meta)]">
                Every garment as it stands: ready, booked, with customers, in the workroom or
                damaged — by design, size and colour.
              </p>
              <Button
                variant="primary"
                disabled={!stock}
                onClick={() => setScreen('stock-summary')}
              >
                Open
              </Button>
            </div>
          </div>
        </Card>
        {revenue && (
          <Card>
            <div className="flex items-start gap-3">
              <span
                className={`flex size-10 shrink-0 items-center justify-center rounded-md ${hueClasses.success.tile}`}
                aria-hidden="true"
              >
                <i className="icon icon-indian-rupee text-lg text-white" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="mb-1 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                  Rental revenue
                </p>
                <p className="text-default mb-3 text-[length:var(--scale-list-meta)]">
                  What the rental invoices earned — by day, month or year, by category and design,
                  against the same days last year.
                </p>
                <Button
                  variant="primary"
                  disabled={!hasFeature(session, 'Billing')}
                  onClick={() => setScreen('rental-revenue')}
                >
                  Open
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
